import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

interface MarqueeProps {
  /** One copy of the looped content — rendered twice, the second aria-hidden. */
  children: ReactNode;
  /** Seconds per full loop. */
  duration?: number;
  reverse?: boolean;
  className?: string;
}

/**
 * The marquee primitive (docs/DESIGN-V9.md §4) — Sakazuki's member walls,
 * MDNT's label strips, Juba's heading echoes, as one reusable structure.
 *
 * Mechanics: a `w-max` flex row of two identical tracks animated
 * `translateX(0 → -50%)`. Because the tracks are equal width and the gap
 * lives *inside* each track (trailing padding on `children`, never a parent
 * gap), -50% is exactly one loop for any content width.
 *
 * Reduced motion: the global media rule freezes animations at their end
 * state; since frame(-50%) ≡ frame(0) for seamless content, the frozen
 * state is indistinguishable from rest — no per-instance fallback needed.
 */
export function Marquee({ children, duration = 40, reverse = false, className }: MarqueeProps) {
  return (
    <div className={cn('overflow-hidden', className)}>
      <div
        className="marquee-track flex w-max"
        style={{ '--marquee-duration': `${duration}s` } as React.CSSProperties}
        data-reverse={reverse || undefined}
      >
        <div className="flex shrink-0">{children}</div>
        <div className="flex shrink-0" aria-hidden="true">
          {children}
        </div>
      </div>
    </div>
  );
}
